import React from 'react';
import { Space, Tag, Select, Button, Popconfirm, message, Popover, Input } from 'antd';
import { InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { ColumnsType } from "antd/es/table";
import { getFormattedDateAndTime } from "../../../../helpers/date_helper";
import { getImageUrl } from "../../../../helpers/image_helper";
import { editDelivery, updatePoDeliveryLegStatus } from "../deliveryApi";
import { PowoDeliveryChallanOpenApi } from "../../../../apis/routes";
import { openAuthedPdf } from "../../../../helpers/pdf_helper";
import { STATUS_TYPES } from "../../../../constants/delivery.constants";
import moment from "moment";

const { Option } = Select;

interface UseDeliveryColumnsProps {
  activeTab: string;
  hasPermission: (permission: string) => boolean;
  setPOData: (data: any) => void;
  setWarehouseDrawerVisible: (visible: boolean) => void;
  setDeliveryLocationDrawerVisible: (visible: boolean) => void;
  setVendor_name: (name: string) => void;
  setItemsToView: (items: any[]) => void;
  setAttachDeliveryDrawerVisible: (id: any) => void;
  removeProof: (id: number, type: "pickup_proof" | "delivery_proof") => void;
  renderLRTags: (id: number) => React.ReactNode;
  renderPOFTags: (id: number) => React.ReactNode;
  fetchPOFData: (id: number) => Promise<any>;
  setAttachLRDrawerVisible: (data: any) => void;
  setAttachPODDrawerVisible: (data: any) => void;
  pofData: Record<number, any>;
  fetchDeliveries: (params: any) => void;
  setPorterLinkDrawerData: (data: any) => void;
  setDispatchTimeDrawerVisible: (data: any) => void;
  setEscalationDrawerData: (data: any) => void;
}

export const useDeliveryColumns = ({
  activeTab,
  hasPermission,
  setPOData,
  setWarehouseDrawerVisible,
  setDeliveryLocationDrawerVisible,
  setVendor_name,
  setItemsToView,
  setAttachDeliveryDrawerVisible,
  removeProof,
  renderLRTags,
  renderPOFTags,
  fetchPOFData,
  setAttachLRDrawerVisible,
  setAttachPODDrawerVisible,
  pofData,
  fetchDeliveries,
  setPorterLinkDrawerData,
  setDispatchTimeDrawerVisible,
  setEscalationDrawerData
}: UseDeliveryColumnsProps) => {

  const commonColumns = ["po_number", "lead_id", "vendor_name", "origin_warehouse", "destination_warehouse",
    "dispatch_time", "delivery_date", "vehicle_number",
    "driver_number", "delivery_service", "po_delivery_status", "action"];

  const isColumnVisible = (columnKey: string) => {
    switch (activeTab) {
      case "deliveries":
        return ["po_number", "service", "service_ref_id", "last_updated_status",
                "created_at", "action"].includes(columnKey);
      case "express":
        return commonColumns.filter(col => col !== "destination_warehouse" && col !== "dispatch_time").includes(columnKey);
      default:
        return commonColumns.includes(columnKey);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Shipment No.",
      key: "po_number",
      render: (data: any) => {
        const transport = data?.po?.estimate?.estimate_additional_charges?.find((charge: any) => charge?.charge_type === "Transport Charges");
        const transportRemarkExists = transport?.remark && transport?.remark?.length > 0;
        const isExpress = data?.is_express;
        const unloading = data?.po?.estimate?.estimate_additional_charges?.find((charge: any) => charge?.charge_type === "Unloading Charges");
        return (
          <div>
            <Space style={{ minWidth: 150 }} size="small">
              <Tag color="geekblue">{data?.po?.type_of_order}</Tag>
              {(data?.po?.po_number ?? "") + (data?.leg_count > 1 && data?.current_leg != null ? "_M" + data.current_leg : "")}
            </Space>
            {data?.po?.estimate?.is_outside_delivery && <Tag color="orange" className="mt-1">Outstation Delivery</Tag>}
            {/* Which warehouse this one is actually leaving. The Origin Warehouse column already
                carries it, but on the outstation tab that is the one fact the raising team needs to
                spot their own rows in a long list, and a column of plain text is what they scroll
                past. Sat next to the Outstation tag, where the eye is already going. */}
            {activeTab === "outstation" && data?.leg_origin_warehouse?.name && (
              <Tag color="volcano" className="mt-1">
                From: {data.leg_origin_warehouse.name}
              </Tag>
            )}
            {transportRemarkExists && <Tag color="orange" className="mt-1">{transport?.remark}</Tag>}
            {data?.po?.estimate?.is_store_pickup && <Tag color="green" className="mt-1">Store pickup</Tag>}
            {isExpress && <Tag color="blue" className="mt-1">Express</Tag>}
            {unloading && <Tag color="cyan" className="mt-1">Unloading</Tag>}
            {(activeTab === "leftovers") && (
              <>
                {data?.po?.estimate?.estimate_additional_charges?.some((charge: any) => 
                  charge?.charge_type?.toLowerCase().includes('sample')) && (
                  <Tag color="purple" className="mt-1">Samples</Tag>
                )}
                {transportRemarkExists && (
                  <Tag color="orange" className="mt-1">
                    {transport?.remark?.includes('Doorstep') ? 'Doorstep' : 'Standard'}
                  </Tag>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Lead ID.",
      key: "lead_id",
      width: 110,
      render: (data: any) => <div>{data?.lead_id}</div>,
    },
    {
      title: "Vendor Name",
      key: "vendor_name",
      render: (data: any) => <div>{data?.vendor_data?.name || "N/A"}</div>,
    },
    {
      title: "Origin Warehouse",
      key: "origin_warehouse",
      render: (data: any) => <div style={{ width: 170 }}>{data?.leg_origin_warehouse?.name || "N/A"}</div>,
    },
    {
      title: "Destination Warehouse",
      key: "destination_warehouse",
      render: (data: any) => <div style={{ width: 170 }}>{data?.leg_destination_warehouse?.name || "N/A"}</div>,
    },
    {
      title: "Dispatch Date",
      key: "dispatch_time",
      render: (data: any) => <div style={{ width: 160 }}>{data?.leg_commited_dispatch_time ? getFormattedDateAndTime(data?.leg_commited_dispatch_time) : 'N/A'}</div>,
    },
    {
      title: "Delivery Date",
      key: "delivery_date",
      render: (data: any) => <div style={{ width: 160 }}>{data?.delivery_planned_eta ? getFormattedDateAndTime(data?.delivery_planned_eta) : getFormattedDateAndTime(data?.po?.delivery_date)}</div>,
    },
    {
      title: "Pickup At",
      key: "pickup_at",
      render: (data: any) => (
        <div style={{ width: 130 }}>
          {data?.pickup_time ? moment(data.pickup_time).format("DD-MM HH:mm") : "N/A"}
        </div>
      ),
    },
    {
      title: "Delivery At",
      key: "delivery_at",
      render: (data: any) => (
        <div style={{ width: 130 }}>
          {data?.delivery_time ? moment(data.delivery_time).format("DD-MM HH:mm") : "N/A"}
        </div>
      ),
    },
    {
      title: "Item Qty",
      key: "item_qty",
      render: (data: any) => {
        const poItems = data?.po?.po_items || [];
        const totalQty = poItems.reduce((sum: number, item: any) => sum + (parseFloat(item?.quantity) || 0), 0);
        return <div>{totalQty || "N/A"}</div>;
      },
    },
    {
      title: "Item Details",
      key: "item_details",
      render: (data: any) => {
        const poItems = data?.po?.po_items || [];
        return (
          <div style={{ width: 200 }}>
            {poItems.slice(0, 2).map((item: any, index: number) => (
              <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>{item?.estimate_item?.private_label_product_name || "N/A"}</div>
            ))}
            {poItems.length > 2 && <div style={{ fontSize: '12px', fontStyle: 'italic' }}>+{poItems.length - 2} more items</div>}
          </div>
        );
      },
    },
    {
      title: "Trip No.",
      key: "trip_no",
      render: (data: any) => <div style={{ width: 120 }}>{data?.trip_name || "N/A"}</div>,
    },
    {
      title: "Delay in Pickup",
      key: "delay_pickup",
      render: (data: any) => {
        if (activeTab !== "delivery_assigned") return null;
        const plannedTime = moment(data?.pickup_planned_eta);
        const actualTime = data?.pickup_time ? moment(data?.pickup_time) : null;
        const delay = actualTime && plannedTime ? actualTime.diff(plannedTime, 'hours') : null;
        return delay ? (
          <div style={{ width: 100, color: delay > 2 ? 'red' : 'orange' }}>{delay > 0 ? `${delay.toFixed(1)}h` : 'On Time'}</div>
        ) : <div style={{ width: 100 }}>N/A</div>;
      },
    },
    {
      title: "Delay in Delivery",
      key: "delay_delivery",
      render: (data: any) => {
        if (activeTab !== "delivery_assigned") return null;
        const plannedTime = moment(data?.delivery_planned_eta);
        const actualTime = data?.delivery_time ? moment(data?.delivery_time) : null;
        const delay = actualTime && plannedTime ? actualTime.diff(plannedTime, 'hours') : null;
        return delay ? (
          <div style={{ width: 100, color: delay > 2 ? 'red' : 'orange' }}>{delay > 0 ? `${delay.toFixed(1)}h` : 'On Time'}</div>
        ) : <div style={{ width: 100 }}>N/A</div>;
      },
    },
    {
      title: "Porter Link",
      key: "porter_link",
      render: (data: any) => {
        if (activeTab !== "delivery_assigned") return null;
        return data?.po?.live_location_link ? (
          <a href={data.po.live_location_link} target="_blank" rel="noopener noreferrer">
            <Tag color="blue" style={{ cursor: 'pointer' }}>View Trip</Tag>
          </a>
        ) : <div>N/A</div>;
      },
    },
    {
      title: "Delivery Person Name",
      key: "delivery_person",
      render: (data: any) => {
        if (activeTab !== "dispatched") return null;
        return <div style={{ width: 150 }}>{data?.delivery_person_name || "N/A"}</div>;
      },
    },
    {
      title: "Vehicle No.",
      key: "vehicle_number",
      render: (data: any) => <div style={{ width: 130 }}>{data?.vehicle_number || "-"}</div>,
    },
    {
      title: "Driver Ph. No.",
      key: "driver_number",
      render: (data: any) => <div style={{ width: 130 }}>{data?.po?.driver_number || "-"}</div>,
    },
    {
      title: "Delivery Service",
      key: "delivery_service",
      render: (data: any) => <div style={{ width: 120 }}><div>{data?.service || "N/A"}</div></div>,
    },
    {
      title: "Remarks/Comments",
      key: "remarks",
      render: (data: any) => <div style={{ width: 150 }}>{data?.delivery_planned_eta_remark || "N/A"}</div>,
    },
    {
      title: "Delivery Status",
      key: "po_delivery_status",
      width: 200,
      render: (data: any) => (
        <>
          <Select
            style={{ width: 180 }}
            disabled={!hasPermission("po.update")}
            onChange={(e) => {
              if (!hasPermission("po.update")) {
                message.error("You don't have permission to update delivery status");
                return;
              }
              setVendor_name(data?.vendor_data?.name)
              if (e === "in_store") {
                setPOData(data);
                setWarehouseDrawerVisible(true);
              } else if (
                (data?.leg_status === "dispatch_pending" && e === "dispatched") ||
                (data?.leg_status === "delivery_assigned" && (e === "dispatched" || e === "delivered"))
              ) {
                setPOData(data);
                setPorterLinkDrawerData({ ...data, targetStatus: e });
              } else if (data?.po?.live_location_link?.length > 0 || e == "dispatch_pending" || (data?.service === "loginext" && data?.leg_status !== "dispatch_pending")) {
                updatePoDeliveryLegStatus(data?.id, e).then(() => fetchDeliveries({ keepCurrentSearch: true }));
              } else {
                setPOData({ ...data });
                setDeliveryLocationDrawerVisible(true);
              }
            }}
            placeholder="Select status"
            value={data?.leg_status}
          >
            {data?.leg_status === "delivery_assigned" && (
              <Option key="delivery_assigned" value="delivery_assigned" disabled>Delivery Assigned</Option>
            )}
            {STATUS_TYPES?.filter((obj: any) => obj?.value !== "in_store" || localStorage.getItem("user_role") === "admin").map((obj: any) => {
              const isDisabled = obj?.disabled || (data?.leg_status === "dispatched" && obj?.value === "pickup_attempted");
              return (
                <Option key={obj?.id} value={obj?.value} disabled={isDisabled}>{obj?.label}</Option>
              );
            })}
          </Select>
        </>
      ),
    },
    {
      title: "Actions",
      key: "action",
      render: (data: any) => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Tag role="button" onClick={() => {
            const poItems = data?.po?.po_items || [];
            setItemsToView(poItems.map((item: any) => {
              const estimateItem = item?.estimate_item;
              return {
                image_url: getImageUrl(estimateItem?.item_image, "200"),
                name: estimateItem?.private_label_product_name,
                category: estimateItem?.category_name,
                quantity: Number(item?.quantity),
              };
            }));
          }}>
            <InfoCircleFilled />
          </Tag>
          <Link to={"/po-details/" + data?.po?.id}>
            <Tag color={"volcano"} style={{ cursor: "pointer" }}>See Details</Tag>
          </Link>
          <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gridAutoFlow: "column", gap: "4px" }}>
          <div onClick={() => openAuthedPdf(PowoDeliveryChallanOpenApi + data?.po?.id).catch(() => {})}>
            <Tag color={"blue"} style={{ cursor: "pointer" }}>Delivery Challan</Tag>
          </div>
          <div onClick={() => openAuthedPdf(PowoDeliveryChallanOpenApi + data?.po?.id + "?is_customer_copy=true").catch(() => {})}>
            <Tag color={"blue"} style={{ cursor: "pointer" }}>Delivery Challan (Customer Copy)</Tag>
          </div>
          <Tag color="red" onClick={() => setEscalationDrawerData(data)} style={{ cursor: "pointer" }}>Raise Escalation</Tag>
          <Tag color="purple" onClick={() => setDispatchTimeDrawerVisible(data)} style={{ cursor: "pointer" }}>Update dispatch time</Tag>
          <Popover
            trigger="click"
            content={
              <form onSubmit={(e) => {
                e.preventDefault();
                if (!hasPermission("delivery.update")) { message.error("You don't have permission to assign a vehicle"); return; }
                const val = (e.currentTarget.elements.namedItem('v_num') as HTMLInputElement).value?.trim();
                if (!val) { message.error("Vehicle number is required"); return; }
                editDelivery({ id: data?.id, vehicle_number: val })
                  .then(() => { message.success("Vehicle assigned successfully"); fetchDeliveries({ keepCurrentSearch: true }); })
                  .catch(() => message.error("Failed to assign vehicle"));
              }}>
                <Space>
                  <Input name="v_num" placeholder="Vehicle #" size="small" defaultValue={data?.vehicle_number} style={{ width: 120 }} />
                  <Button type="primary" size="small" htmlType="submit">Assign</Button>
                </Space>
              </form>
            }
          >
            <Tag color="cyan" style={{ cursor: "pointer" }}>Assign Vehicle Number</Tag>
          </Popover>
          <Tag color="geekblue" onClick={() => setAttachDeliveryDrawerVisible(data?.po?.id)} style={{ cursor: "pointer" }}>Attach delivery</Tag>
          {data?.pickup_proof && (
            <a href={data?.pickup_proof} target="_blank" rel="noreferrer">
              <Tag color="green" closable={hasPermission("delivery.update")} onClose={(e) => { e.preventDefault(); if (!hasPermission("delivery.update")) { message.error("You don't have permission to remove proof"); return; } removeProof(data?.id, "pickup_proof"); }}>Pickup Proof</Tag>
            </a>
          )}
          {data?.delivery_proof && (
            <a href={data?.delivery_proof} target="_blank" rel="noreferrer">
              <Tag color="green" closable={hasPermission("delivery.update")} onClose={(e) => { e.preventDefault(); if (!hasPermission("delivery.update")) { message.error("You don't have permission to remove proof"); return; } removeProof(data?.id, "delivery_proof"); }} closeIcon>Delivery Proof (Legacy)</Tag>
            </a>
          )}
          {renderLRTags(data?.id)}
          {renderPOFTags(data?.id)}
          {data?.leg_status === "delivered" && (() => {
            const pofData_item = pofData[data?.id];
            const existingLRProofs = pofData_item?.proofs?.filter((proof: any) => !proof.is_deleted && proof.fulfillment_type === 'LR') || [];
            return existingLRProofs.length === 0 && (
              <Tag color="orange" onClick={() => setAttachLRDrawerVisible(data)} style={{ cursor: "pointer" }}>Attach LR</Tag>
            );
          })()}
          {data?.leg_status === "delivered" && (
            <Tag color="red" onClick={async () => {
                await fetchPOFData(data?.id);
                setAttachPODDrawerVisible(data);
              }}
              style={{ cursor: "pointer" }}
            >Add POD</Tag>
          )}
          </div>
        </div>
      ),
    },
  ].filter(column => isColumnVisible(column.key as string));

  const deliveriesColumns = [
    {
      title: "PO No.",
      key: "po_number",
      render: (data: any) => (
        <Space>
          <span>{data?.po?.po_number}</span>
          {data?.geocoding_error && <WarningFilled style={{ color: "red" }} />}
        </Space>
      ),
    },
    { title: "Service", key: "service", dataIndex: "service" },
    { title: "Service Ref ID", key: "service_ref_id", dataIndex: "service_ref_id" },
    {
      title: "Last Updated Status",
      key: "last_updated_status",
      render: (data: any) => {
        if (data?.is_deleted) return "Deleted";
        return (
          <Select
            value={data?.last_updated_status}
            style={{ width: 200 }}
            onChange={(value) => {
              if (!hasPermission("delivery.update")) {
                message.error("You don't have permission to update delivery status");
                return;
              }
              editDelivery({ id: data?.id, last_updated_status: value });
              fetchDeliveries({ keepCurrentSearch: true, page: 1 });
            }}
            disabled={!hasPermission("delivery.update") || ["push_error", "delivered"].includes(data?.last_updated_status)}
          >
            <Option value="created">Created</Option>
            <Option value="in_store">In Warehouse</Option>
            <Option value="picked_up">Picked Up</Option>
            <Option value="in_transit">In Transit</Option>
            <Option value="delivered">Delivered</Option>
            <Option value="push_error">Push Error</Option>
          </Select>
        );
      },
    },
    { title: "Created At", key: "created_at", render: (data: any) => getFormattedDateAndTime(data?.created_at) },
    {
      title: "Actions",
      key: "actions",
      render: (data: any) => (
        <Space>
          <Popconfirm
            disabled={!hasPermission("delivery.update")}
            onConfirm={() => {
              if (!hasPermission("delivery.update")) {
                message.error("You don't have permission to cancel this delivery");
                return;
              }
              editDelivery({ id: data?.id, is_deleted: true });
              fetchDeliveries({ keepCurrentSearch: true, page: 1 });
            }}
            title="Are you sure you want to delete this delivery?"
          >
            <Button size="small" danger disabled={!hasPermission("delivery.update")}>Cancel Delivery</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(column => isColumnVisible(column.key));

  return { columns, deliveriesColumns };
};
